/**
 * Toast Notification Components
 *
 * Bottom-center positioned toast notifications with accessibility support.
 * Positioned in line with FAB (bottom: spacing.xl).
 */

import React from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { tokens } from '../styles/tokens';
import type { Toast as ToastType, ToastType as ToastVariant } from '../hooks/useToast';

interface ToastProps {
  toast: ToastType;
  onDismiss: () => void;
}

const getToastStyles = (type: ToastVariant) => {
  switch (type) {
    case 'success':
      return {
        background: tokens.colors.successBg,
        border: tokens.colors.successFg,
        icon: tokens.colors.successFg,
      };
    case 'error':
      return {
        background: tokens.colors.criticalBg,
        border: tokens.colors.criticalFg,
        icon: tokens.colors.criticalFg,
      };
    default:
      return {
        background: tokens.colors.infoBg,
        border: tokens.colors.infoFg,
        icon: tokens.colors.infoFg,
      };
  }
};

const ToastIcon: React.FC<{ type: ToastVariant; color: string }> = ({ type, color }) => {
  const iconStyle = { width: '16px', height: '16px', color, flexShrink: 0 };

  switch (type) {
    case 'success':
      return <CheckCircle style={iconStyle} />;
    case 'error':
      return <AlertCircle style={iconStyle} />;
    default:
      return <Info style={iconStyle} />;
  }
};

export const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const styles = getToastStyles(toast.type);

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing.s,
        padding: `${tokens.spacing.s} ${tokens.spacing.m}`,
        background: styles.background,
        border: `1px solid ${styles.border}`,
        borderRadius: tokens.borders.radiusContainer,
        boxShadow: tokens.elevation.overlay,
        fontSize: tokens.typography.fontSize.s,
        fontFamily: tokens.typography.fontFamily,
        color: tokens.colors.textDefault,
        animation: 'slideInUp 200ms ease-out',
      }}
    >
      <ToastIcon type={toast.type} color={styles.icon} />

      <span style={{ flex: 1 }}>{toast.message}</span>

      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          borderRadius: tokens.borders.radiusDefault,
          color: tokens.colors.textSecondary,
          transition: `background ${tokens.animation.duration} ${tokens.animation.timing}`,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = tokens.colors.interactiveHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <X style={{ width: '14px', height: '14px' }} />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastType[];
  onDismiss: (id: string) => void;
  isSidebarOpen: boolean;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss, isSidebarOpen }) => {
  if (toasts.length === 0) return null;

  return (
    <>
      {/* Keyframe animation injected via style tag */}
      <style>
        {`
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      <div
        data-multidev-sdk="true"
        style={{
          position: 'fixed',
          bottom: tokens.spacing.xl,
          // Center in effective viewport: offset by half sidebar width when open
          left: isSidebarOpen ? `calc(50% - calc(${tokens.layout.sidebarWidth} / 2))` : '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.spacing.s,
          zIndex: 1000001, // Above FAB (1000000)
          pointerEvents: 'auto',
          // Animate position during sidebar transition
          transition: `left ${tokens.animation.durationMs}ms ${tokens.animation.timingRaw}`,
        }}
      >
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
        ))}
      </div>
    </>
  );
};
